import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { stateful, useState } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { endOfDay, endOfMonth, startOfDay, startOfMonth } from 'date-fns';
import { html, nothing } from 'lit';
import { BillingSummary, getBillingSummary } from '../../api/billing.js';

export const Kpis = stateful(() => {
  // Month selection state - only current and previous month
  const currentDate = new Date();
  const currentMonth = (currentDate.getMonth() + 1).toString().padStart(2, '0');
  const previousMonth = currentDate.getMonth() === 0 ? '12' : currentDate.getMonth().toString().padStart(2, '0');
  const previousMonthYear =
    currentDate.getMonth() === 0 ? (currentDate.getFullYear() - 1).toString() : currentDate.getFullYear().toString();

  const [selectedYear, setSelectedYear] = useState<string>(currentDate.getFullYear().toString());
  const [selectedMonth, setSelectedMonth] = useState<string>(currentMonth);
  const [activeTab, setActiveTab] = useState<'current' | 'previous' | 'daily'>('current');
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());

  // Use useState for the selected conversation and filters
  // const ratingsQuery = useQuery(() => averageRatings()(selectionSet(() => RatingSummaryEntity, 2)), []);
  const billingQuery = useQuery(() => {
    if (activeTab === 'daily' && selectedDate) {
      return getBillingSummary(startOfDay(selectedDate), endOfDay(selectedDate))(selectionSet(() => BillingSummary, 2));
    }

    const monthStart = startOfMonth(new Date(Number(selectedYear), Number(selectedMonth) - 1, 1));
    const monthEnd = endOfMonth(monthStart);

    return getBillingSummary(monthStart, monthEnd)(selectionSet(() => BillingSummary, 2));
  }, [activeTab, selectedYear, selectedMonth, selectedDate]);
  const [showBillingDetails, setShowBillingDetails] = useState<boolean>(false);

  return html`
    <div class="flex flex-col gap-6 p-6 h-full overflow-y-auto">
      <h2 class="text-3xl font-extrabold text-primary mb-2">Source Statistics</h2>

      <!-- Contact Enrichment Accuracy - Top KPI -->
      <div
        class="card bg-gradient-to-r from-success/10 to-success/5 shadow-xl rounded-2xl border border-success/30 hover:scale-[1.02] transition-transform duration-200"
      >
        <div class="card-body items-center text-center py-4">
          <div class="flex items-center justify-center gap-4 w-full">
            <div>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-8 w-8 text-success"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <div class="flex-1">
              <h3 class="text-sm font-medium text-base-content/70 uppercase tracking-wide mb-1">
                Contact Enrichment Attribute Extraction Accuracy
              </h3>
              <div
                class="tooltip tooltip-bottom"
                data-tip="Calculated by running automated tests over 13 test cases with 17 attributes to extract each"
              >
                <p class="text-3xl font-bold text-success">99.5%</p>
              </div>
              <p class="text-xs text-base-content/60 mt-1">Last tested: Nov 6, 2025</p>
            </div>
            <div class="badge badge-success badge-lg">Excellent</div>
          </div>
        </div>
      </div>

      <!-- Billing Overview Dashboard -->
      <div
        class="card bg-gradient-to-r from-info/10 to-info/5 shadow-xl rounded-2xl border border-info/30 hover:scale-[1.02] transition-transform duration-200"
      >
        <div class="card-body p-6">
          <div class="flex items-center justify-between mb-4">
            <h3 class="text-xl font-bold text-info flex items-center gap-2">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                />
              </svg>
              Billing Overview
            </h3>
            <div class="flex items-center gap-2">
              <div class="flex flex-col gap-2">
                <div class="tabs tabs-boxed">
                  <a
                    class="tab ${activeTab === 'current' ? 'tab-active' : ''}"
                    @click=${() => {
                      setActiveTab('current');
                      setSelectedMonth(currentMonth);
                      setSelectedYear(currentDate.getFullYear().toString());
                    }}
                  >
                    ${new Date().toLocaleString('default', { month: 'short' })} ${currentDate.getFullYear()}
                  </a>
                  <a
                    class="tab ${activeTab === 'previous' ? 'tab-active' : ''}"
                    @click=${() => {
                      setActiveTab('previous');
                      setSelectedMonth(previousMonth);
                      setSelectedYear(previousMonthYear);
                    }}
                  >
                    ${new Date(currentDate.getFullYear(), currentDate.getMonth() - 1).toLocaleString('default', {
                      month: 'short',
                    })}
                    ${previousMonthYear}
                  </a>
                  <a
                    class="tab ${activeTab === 'daily' ? 'tab-active' : ''}"
                    @click=${() => {
                      setActiveTab('daily');
                      setSelectedDate(new Date());
                    }}
                  >
                    Daily
                  </a>
                </div>
                ${activeTab === 'daily'
                  ? html`
                      <input
                        class="input input-sm input-bordered"
                        type="date"
                        value=${selectedDate.toISOString().slice(0, 10)}
                        @input=${(e: Event) => {
                          const nextValue = (e.target as HTMLInputElement | null)?.value;
                          if (nextValue) setSelectedDate(new Date(nextValue));
                        }}
                      />
                    `
                  : nothing}
              </div>
              <button class="btn btn-sm btn-primary" @click=${() => setShowBillingDetails(!showBillingDetails)}>
                ${showBillingDetails ? 'HIDE DETAILS' : 'SHOW DETAILS'}
              </button>
            </div>
          </div>

          ${billingQuery.loading
            ? html`
                <div class="flex items-center justify-center p-10">
                  <span class="loading loading-spinner loading-lg text-primary"></span>
                </div>
              `
            : html` <!-- Billing Summary Cards -->
                <div class="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-4">
                  ${billingQuery.value?.intercom
                    ? html`
                        <div class="bg-accent/10 rounded-lg p-3 text-center">
                          <div class="text-xs text-accent/70 font-medium">Intercom</div>
                          <div class="text-lg font-bold text-accent">
                            ${(billingQuery.value.intercom.countWithRating7 || 0) +
                            (billingQuery.value.intercom.countWithRating8 || 0) +
                            (billingQuery.value.intercom.countWithRating9 || 0) +
                            (billingQuery.value.intercom.countWithRating10 || 0)}
                          </div>
                          <div class="text-xs text-base-content/60">billable conversations</div>
                        </div>
                      `
                    : nothing}
                  ${billingQuery.value?.odoo
                    ? html`
                        <div class="bg-secondary/10 rounded-lg p-3 text-center">
                          <div class="text-xs text-secondary/70 font-medium">Mail/Odoo</div>
                          <div class="text-lg font-bold text-secondary">
                            ${(billingQuery.value.odoo.countWithRating7 || 0) +
                            (billingQuery.value.odoo.countWithRating8 || 0) +
                            (billingQuery.value.odoo.countWithRating9 || 0) +
                            (billingQuery.value.odoo.countWithRating10 || 0)}
                          </div>
                          <div class="text-xs text-base-content/60">billable replies</div>
                        </div>
                      `
                    : nothing}
                  ${billingQuery.value?.contact
                    ? html`
                        <div class="bg-success/10 rounded-lg p-3 text-center">
                          <div class="text-xs text-success/70 font-medium">Contacts</div>
                          <div class="text-lg font-bold text-success">${billingQuery.value.contact.count || 0}</div>
                          <div class="text-xs text-base-content/60">enriched contacts</div>
                        </div>
                      `
                    : nothing}
                  ${billingQuery.value?.tag
                    ? html`
                        <div class="bg-warning/10 rounded-lg p-3 text-center">
                          <div class="text-xs text-warning/70 font-medium">Tags</div>
                          <div class="text-lg font-bold text-warning">${billingQuery.value.tag.count || 0}</div>
                          <div class="text-xs text-base-content/60">tag assignments</div>
                        </div>
                      `
                    : nothing}
                  ${billingQuery.value?.priority
                    ? html`
                        <div class="bg-error/10 rounded-lg p-3 text-center">
                          <div class="text-xs text-error/70 font-medium">Priority</div>
                          <div class="text-lg font-bold text-error">
                            ${billingQuery.value.priority.messageCount || 0}
                          </div>
                          <div class="text-xs text-base-content/60">prioritisations</div>
                        </div>
                      `
                    : nothing}
                  ${billingQuery.value?.mailFiltering
                    ? html`
                        <div class="bg-info/10 rounded-lg p-3 text-center">
                          <div class="text-xs text-info/70 font-medium">Spam</div>
                          <div class="text-lg font-bold text-info">
                            ${billingQuery.value.mailFiltering.messageCount || 0}
                          </div>
                          <div class="text-xs text-base-content/60">filtered emails</div>
                        </div>
                      `
                    : nothing}
                </div>

                <!-- Detailed Billing Information -->
                ${showBillingDetails
                  ? html`
                      <div class="bg-base-100 rounded-lg p-4 border border-base-300">
                        <h4 class="text-sm font-semibold text-base-content mb-3">
                          ${activeTab === 'daily'
                            ? html`Detailed Billing Information for ${selectedDate}`
                            : html`Detailed Billing Information for ${selectedMonth}/${selectedYear}`}
                        </h4>
                        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                          ${billingQuery.value?.intercom
                            ? html`
                                <div class="bg-accent/5 rounded-lg p-3">
                                  <h5 class="text-sm font-medium text-accent mb-2">Intercom Conversations</h5>
                                  <div class="text-xs space-y-1">
                                    <div class="flex justify-between">
                                      <span>Total Conversations:</span
                                      ><span class="font-mono"
                                        >${billingQuery.value.intercom.conversationCount || 0}</span
                                      >
                                    </div>
                                    <div class="flex justify-between">
                                      <span>Total Messages:</span
                                      ><span class="font-mono">${billingQuery.value.intercom.messageCount || 0}</span>
                                    </div>
                                    <div class="flex justify-between">
                                      <span>With Rating ≥7:</span
                                      ><span class="font-mono"
                                        >${(billingQuery.value.intercom.countWithRating7 || 0) +
                                        (billingQuery.value.intercom.countWithRating8 || 0) +
                                        (billingQuery.value.intercom.countWithRating9 || 0) +
                                        (billingQuery.value.intercom.countWithRating10 || 0)}</span
                                      >
                                    </div>
                                    <div class="flex justify-between">
                                      <span>With Rating ≥5:</span
                                      ><span class="font-mono"
                                        >${billingQuery.value.intercom.countWithRating5 || 0}</span
                                      >
                                    </div>
                                    <div class="flex justify-between">
                                      <span>Average Rating:</span
                                      ><span class="font-mono">
                                        ${billingQuery.value.intercom.averageRating?.toFixed(1)}
                                      </span>
                                    </div>
                                    <div
                                      class="flex justify-between font-bold text-success border-t border-base-300 pt-1"
                                    >
                                      <span>Billable Transactions:</span
                                      ><span class="font-mono"
                                        >${(billingQuery.value.intercom.countWithRating7 || 0) +
                                        (billingQuery.value.intercom.countWithRating8 || 0) +
                                        (billingQuery.value.intercom.countWithRating9 || 0) +
                                        (billingQuery.value.intercom.countWithRating10 || 0)}</span
                                      >
                                    </div>
                                  </div>
                                </div>
                              `
                            : nothing}
                          ${billingQuery.value?.odoo
                            ? html`
                                <div class="bg-secondary/5 rounded-lg p-3">
                                  <h5 class="text-sm font-medium text-secondary mb-2">Mail/Odoo Conversations</h5>
                                  <div class="text-xs space-y-1">
                                    <div class="flex justify-between">
                                      <span>Total Received E-Mails:</span
                                      ><span class="font-mono">${billingQuery.value.numberOfMailsReceived || 0}</span>
                                    </div>
                                    <div class="flex justify-between">
                                      <span>Total Received E-Mails with Tickets:</span
                                      ><span class="font-mono"
                                        >${billingQuery.value.numberOfMailsWithTickets || 0}</span
                                      >
                                    </div>
                                    <div class="flex justify-between">
                                      <span>Irrelevant E-Mails:</span
                                      ><span class="font-mono">${billingQuery.value.irrelevantMailCount || 0}</span>
                                    </div>
                                    <div class="flex justify-between">
                                      <span>Total Conversations:</span
                                      ><span class="font-mono">${billingQuery.value.odoo.conversationCount || 0}</span>
                                    </div>
                                    <!-- <div class="flex justify-between">
                                <span>Total Messages:</span
                                ><span class="font-mono">${billingQuery.value.odoo.messageCount || 0}</span>
                              </div> -->
                                    <div class="flex justify-between">
                                      <span>No Timestamp:</span
                                      ><span class="font-mono"
                                        >${billingQuery.value.odoo.noTimestampMessageCount || 0}</span
                                      >
                                    </div>
                                    <div class="flex justify-between">
                                      <span>With Rating ≥7:</span
                                      ><span class="font-mono"
                                        >${(billingQuery.value.odoo.countWithRating7 || 0) +
                                        (billingQuery.value.odoo.countWithRating8 || 0) +
                                        (billingQuery.value.odoo.countWithRating9 || 0) +
                                        (billingQuery.value.odoo.countWithRating10 || 0)}</span
                                      >
                                    </div>
                                    <div class="flex justify-between">
                                      <span>Average Rating:</span
                                      ><span class="font-mono">
                                        ${billingQuery.value.odoo.averageRating?.toFixed(1)}
                                      </span>
                                    </div>
                                    <div
                                      class="flex justify-between font-bold text-success border-t border-base-300 pt-1"
                                    >
                                      <span>Billable Transactions:</span
                                      ><span class="font-mono"
                                        >${(billingQuery.value.odoo.countWithRating7 || 0) +
                                        (billingQuery.value.odoo.countWithRating8 || 0) +
                                        (billingQuery.value.odoo.countWithRating9 || 0) +
                                        (billingQuery.value.odoo.countWithRating10 || 0)}</span
                                      >
                                    </div>
                                  </div>
                                </div>
                              `
                            : nothing}
                          ${billingQuery.value?.contact
                            ? html`
                                <div class="bg-success/5 rounded-lg p-3">
                                  <h5 class="text-sm font-medium text-success mb-2">Contact Enrichment</h5>
                                  <div class="text-xs space-y-1">
                                    <div class="flex justify-between">
                                      <span>Total Contacts:</span
                                      ><span class="font-mono">${billingQuery.value.contact.count || 0}</span>
                                    </div>
                                    <div
                                      class="flex justify-between font-bold text-success border-t border-base-300 pt-1"
                                    >
                                      <span>Billable Transactions:</span
                                      ><span class="font-mono">${billingQuery.value.contact.count || 0}</span>
                                    </div>
                                  </div>
                                </div>
                              `
                            : nothing}
                          ${billingQuery.value?.tag
                            ? html`
                                <div class="bg-warning/5 rounded-lg p-3">
                                  <h5 class="text-sm font-medium text-warning mb-2">Tag Assignment</h5>
                                  <div class="text-xs space-y-1">
                                    <div class="flex justify-between">
                                      <span>Total Tag Assignments:</span
                                      ><span class="font-mono">${billingQuery.value.tag.count || 0}</span>
                                    </div>
                                    <div
                                      class="flex justify-between font-bold text-success border-t border-base-300 pt-1"
                                    >
                                      <span>Billable Transactions:</span
                                      ><span class="font-mono">${billingQuery.value.tag.count || 0}</span>
                                    </div>
                                  </div>
                                </div>
                              `
                            : nothing}
                          ${billingQuery.value?.priority
                            ? html`
                                <div class="bg-error/5 rounded-lg p-3">
                                  <h5 class="text-sm font-medium text-error mb-2">Priority Processing</h5>
                                  <div class="text-xs space-y-1">
                                    <div class="flex justify-between">
                                      <span>Standalone priotisation processes:</span
                                      ><span class="font-mono">${billingQuery.value.priority.messageCount || 0}</span>
                                    </div>
                                    <div
                                      class="flex justify-between font-bold text-success border-t border-base-300 pt-1"
                                    >
                                      <span>Billable Transactions:</span
                                      ><span class="font-mono">${billingQuery.value.priority.messageCount || 0}</span>
                                    </div>
                                  </div>
                                </div>
                              `
                            : nothing}
                          ${billingQuery.value?.mailFiltering
                            ? html`
                                <div class="bg-info/5 rounded-lg p-3">
                                  <h5 class="text-sm font-medium text-info mb-2">Mail Filtering</h5>
                                  <div class="text-xs space-y-1">
                                    <div class="flex justify-between">
                                      <span>Spam Filtered:</span
                                      ><span class="font-mono"
                                        >${billingQuery.value.mailFiltering.messageCount || 0}</span
                                      >
                                    </div>
                                    <div
                                      class="flex justify-between font-bold text-success border-t border-base-300 pt-1"
                                    >
                                      <span>Billable Transactions:</span
                                      ><span class="font-mono"
                                        >${billingQuery.value.mailFiltering.messageCount || 0}</span
                                      >
                                    </div>
                                  </div>
                                </div>
                              `
                            : nothing}
                        </div>
                      </div>
                    `
                  : nothing}`}
        </div>
      </div>
    </div>
  `;
});
